import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { isApiError } from '@/api/errors'
import { AuthProvider } from '@/auth/AuthProvider'
import { ToastProvider } from '@/shared/ui/toast/ToastProvider'

import { ThemeProvider } from './ThemeProvider'

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Una dashboard di lavoro non deve ricaricarsi ogni volta che si torna
        // dalla finestra accanto: si perde la posizione senza guadagnare nulla.
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Crediti finiti o modulo spento non migliorano riprovando: riprovare
          // significherebbe solo far aspettare per lo stesso rifiuto.
          if (isApiError(error) && error.status >= 400 && error.status < 500) return false
          return failureCount < 2
        },
      },
      mutations: {
        retry: false,
      },
    },
  })
}

export function AppProviders({ children }: { children: ReactNode }) {
  // Creato una volta sola: un client nuovo a ogni render svuoterebbe la cache.
  const [queryClient] = useState(createQueryClient)

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>{children}</AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}
